import React, { useState, useEffect } from 'react';
import api from '@/api';
import { Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

export default function AccessLog() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [devices, setDevices] = useState([]);
  const [filterDevice, setFilterDevice] = useState('');

  const load = async (p = 1, device = filterDevice) => {
    setLoading(true);
    try {
      const res = await api.get(`/access-log?page=${p}&limit=50&device_id=${device}`);
      setLogs(p === 1 ? res.data.items : prev => [...prev, ...res.data.items]);
      setTotal(res.data.total);
    } catch { } finally { setLoading(false); }
  };

  useEffect(() => {
    api.get('/devices').then(r => setDevices(r.data)).catch(() => {});
    load(1);
  }, []);

  useEffect(() => { setPage(1); load(1, filterDevice); }, [filterDevice]);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    load(next);
  };

  const clearLogs = async () => {
    if (!confirm('Eliminare tutti i log?')) return;
    try {
      await api.delete('/access-log');
      setLogs([]);
      setTotal(0);
      toast.success('Log eliminati');
    } catch { toast.error('Errore'); }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString('it-IT'); } catch { return iso; }
  };

  const hasMore = logs.length < total;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="label-gold">Sistema</p>
          <h2 className="font-serif text-3xl text-white font-light mt-1">Log Accessi</h2>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setPage(1); load(1); }} className="btn-ghost p-2" data-testid="refresh-log-btn">
            <RefreshCw size={16} />
          </button>
          {user?.role === 'superadmin' && (
            <button onClick={clearLogs} className="px-4 py-2 border border-red-800 text-red-400 text-xs uppercase tracking-widest hover:bg-red-900/20 transition-colors"
              data-testid="clear-logs-btn">
              Elimina tutti
            </button>
          )}
        </div>
      </div>
      <div className="flex gap-3 flex-wrap">
        <select className="input-dark max-w-xs" value={filterDevice} onChange={e => setFilterDevice(e.target.value)} data-testid="filter-device">
          <option value="">Tutti i dispositivi</option>
          {devices.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <span className="text-[var(--lux-text-muted-3)] text-sm self-center">{total} eventi totali</span>
      </div>
      {loading && page === 1 ? (
        <div className="flex justify-center py-16"><Loader2 size={28} className="animate-spin text-[#c9a84c]" /></div>
      ) : (
        <div className="card-dark rounded-lg overflow-hidden">
          {logs.length === 0 ? (
            <div className="p-12 text-center"><p className="text-[var(--lux-text-muted-3)] text-sm">Nessun evento registrato.</p></div>
          ) : (
            <>
              <table className="w-full table-dark">
                <thead><tr>
                  <th className="text-left">Dispositivo</th>
                  <th className="text-left hidden sm:table-cell">Azione</th>
                  <th className="text-left hidden md:table-cell">Ospite</th>
                  <th className="text-left">Esito</th>
                  <th className="text-left hidden md:table-cell">Tempo</th>
                  <th className="text-left">Data</th>
                </tr></thead>
                <tbody>
                  {logs.map(log => (
                    <tr key={log.id}>
                      <td className="text-white text-sm">{log.device_name || '—'}</td>
                      <td className="hidden sm:table-cell">
                        <span className="text-xs px-2 py-0.5 bg-[var(--lux-surface-3)] rounded text-[var(--lux-text-sec)] capitalize">{log.action}</span>
                      </td>
                      <td className="hidden md:table-cell text-[var(--lux-text-muted-2)] text-xs">{log.actor_name || '—'}</td>
                      <td>
                        <span className={`w-2 h-2 rounded-full inline-block ${log.success ? 'bg-green-500' : 'bg-red-500'}`} />
                      </td>
                      <td className="hidden md:table-cell text-[var(--lux-text-muted-3)] text-xs">{log.response_ms ? `${log.response_ms}ms` : '—'}</td>
                      <td className="text-[var(--lux-text-muted-2)] text-xs">{fmtDate(log.timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {hasMore && (
                <div className="p-4 text-center">
                  <button onClick={loadMore} disabled={loading} className="btn-outline-gold text-xs flex items-center gap-2 mx-auto" data-testid="load-more-logs-btn">
                    {loading ? <Loader2 size={12} className="animate-spin" /> : null}
                    Carica altri ({total - logs.length} rimanenti)
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
